import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ChevronDown, ChevronUp, ShieldCheck, Info, AlertTriangle, ExternalLink } from "lucide-react";

export type Citation = {
  n: number;
  document_id: string;
  title: string;
  organisation: string;
  jurisdiction: string;
  section: string | null;
  page_start: number | null;
  page_end: number | null;
  version: string | null;
  source_url: string | null;
  published_at: string | null;
  reviewed_at: string | null;
};

export type Confidence = "evidence_based" | "general_educational" | "seek_professional";

const LABELS: Record<Confidence, { text: string; className: string; icon: React.ComponentType<{ className?: string }> }> = {
  evidence_based: {
    text: "Evidence-based",
    className: "bg-emerald-50 text-emerald-800 border-emerald-200 dark:bg-emerald-950 dark:text-emerald-200 dark:border-emerald-800",
    icon: ShieldCheck,
  },
  general_educational: {
    text: "General educational information",
    className: "bg-amber-50 text-amber-800 border-amber-200 dark:bg-amber-950 dark:text-amber-200 dark:border-amber-800",
    icon: Info,
  },
  seek_professional: {
    text: "Medical review recommended",
    className: "bg-rose-50 text-rose-800 border-rose-200 dark:bg-rose-950 dark:text-rose-200 dark:border-rose-800",
    icon: AlertTriangle,
  },
};

export function AuraCitations({ confidence, citations }: { confidence: Confidence; citations: Citation[] }) {
  const [open, setOpen] = useState(false);
  const meta = LABELS[confidence];
  const Icon = meta.icon;

  return (
    <div className="mt-2 space-y-2">
      <Badge variant="outline" className={`gap-1 ${meta.className}`}>
        <Icon className="h-3 w-3" />
        {meta.text}
      </Badge>

      {citations.length > 0 && (
        <div className="text-xs">
          <Button
            variant="ghost"
            size="sm"
            className="h-auto p-1 text-xs text-muted-foreground"
            onClick={() => setOpen((o) => !o)}
          >
            {open ? <ChevronUp className="h-3 w-3 mr-1" /> : <ChevronDown className="h-3 w-3 mr-1" />}
            {citations.length} source{citations.length === 1 ? "" : "s"}
          </Button>
          {open && (
            <ul className="mt-1 space-y-2 border-l-2 border-muted pl-3">
              {citations.map((c) => (
                <li key={c.n} className="text-xs text-muted-foreground">
                  <span className="font-medium text-foreground">[{c.n}] {c.organisation}</span>
                  {" — "}{c.title}
                  {c.version && <span className="italic"> (v{c.version})</span>}
                  <div className="flex flex-wrap gap-1 mt-0.5">
                    <Badge variant="outline" className="text-[10px] px-1 py-0">{c.jurisdiction}</Badge>
                    {c.section && <Badge variant="outline" className="text-[10px] px-1 py-0">{c.section}</Badge>}
                    {c.reviewed_at && <span className="text-[10px]">Reviewed: {c.reviewed_at}</span>}
                    {c.source_url && (
                      <a href={c.source_url} target="_blank" rel="noopener noreferrer" className="text-[10px] inline-flex items-center gap-0.5 text-primary hover:underline">
                        Source <ExternalLink className="h-2.5 w-2.5" />
                      </a>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {confidence === "seek_professional" && citations.length === 0 && (
        <div className="text-xs text-muted-foreground italic">
          For personalised guidance, please speak with a menopause-trained GP or clinician.
        </div>
      )}
    </div>
  );
}
